import {users as usersShared} from '@cl/shared';
import {doesUserHaveRole} from 'common/src/modules/roles/utils/doesUserHaveRole';
import {AgentName} from 'common/src/modules/shared/copilot/copilot.enums';
import {Agent, ConversationStarter} from 'common/src/modules/shared/copilot/copilot.types';
import agent from 'common/src/modules/shared/copilot/utils/creators/agent';
import {RoleTypeName} from 'common/src/modules/users/users.types';
import {CLStore} from 'common/src/redux/store';
import {AiAnalyticsEventType} from '../../../analytics.types';
import {inlineWidgetSchema} from '../../copilotAnalytics.schemas';
import getBestPracticesTool from '../../tools/getBestPracticesTool';
import {AccountAnalyticsToolName} from './accountAnalyticsAgent.enums';
import accountAnalyticsContext from './contexts/accountAnalyticsContext';
import accountAnalyticsDataTool from './tools/accountAnalyticsDataTool';
import accountAnalyticsTableDataTool from './tools/accountAnalyticsTableDataTool';
import accountTopPerformingTool from './tools/accountTopPerformingTool';
import accountWorstPerformingTool from './tools/accountWorstPerformingTool';
import checkAccountWidgetExistsTool from './tools/checkAccountWidgetExistsTool';
import createAccountDashboardTool from './tools/createAccountDashboardTool';
import createAccountWidgetTool from './tools/createAccountWidgetTool';
import deleteAccountDashboardTool from './tools/deleteAccountDashboardTool';
import deleteAccountWidgetTool from './tools/deleteAccountWidgetTool';
import getAccountFilterValuesTool from './tools/getAccountFilterValuesTool';
import highlightAccountWidgetTool from './tools/highlightAccountWidgetTool';
import navigateToAccountDashboardTool from './tools/navigateToAccountDashboardTool';
import renameAccountDashboardTool from './tools/renameAccountDashboardTool';
import updateAccountWidgetTool from './tools/updateAccountWidgetTool';

function accountAnalyticsAgent(store: CLStore): Agent {
  const conversationStarters: ConversationStarter[] = [];

  const userHasAnalyticsWriteRole = doesUserHaveRole(
    usersShared.userSubject.data,
    RoleTypeName.AnalyticsWrite,
  );

  const role =
    'You are a account analytics agent who fetches and analyzes account data, and keeps dashboards relevant by adding, updating, or removing widgets as needed';

  const inScope = [
    'Answer questions about account (general) analytics, metrics and dimensions',
    'Converse campaign agnostic analytics tasks and questions',
    'Managing account level dashboards and widgets',
  ];

  const outOfScope = [
    'Anything related to campaign level analytics',
    'Analytics for a specific campaign',
  ];

  const instructions = `
    1. Prerequisite:  
      a. In order to fetch analytics data for an account, you need to be on the account dashboard page, use ${AccountAnalyticsToolName.NavigateToAccountDashboard} tool to navigate to the account dashboard page.
      b. Each widget MUST have exactly ONE eventType.If multiple eventTypes are requested, the agent MUST create separate widgets — one per eventType.

    2. How to fetch Analytics data:
        - Date range: If the user requested a specific one use it, otherwise, take the dates range from the account context
        - Always use analytics event type ${AiAnalyticsEventType.Adserving} by default if not specified by the user.
        - Metrics: only choose from the "Available metrics" list by event type (don't invent your own metrics they will not be supported)
        - Viewing dimensions: only choose from the "Available viewing dimensions" list by event type
        - Filtering dimensions (A filter is not mandatory, but if the user requested one, you must):
            - only choose a dimension from the "Available filtering dimensions" list by event type
            - only choose values from allowed values for that dimension (Never assume that a value is valid, even if it is common)
            - if the requested value is not in the allowed list, do not apply the filter. Instead, return the list of valid options to the user and request clarification.

    3. How to answer user queries:
        - First respond with a textual answer with analysis which includes the dates range and the name of the requested query.
        - Then show an "inline widget" when possible in this manner:
          - When the user requests a specific widget type, generate that widget if it is technically supported. Before generating it, check the widget's known limitations. If the requested widget is not supported because of a limitation, clearly explain which limitation applies and provide a valid alternative configuration.
          - A title which contains metrics and dimensions. The number of metrics in the title MUST NEVER be greater than 2.
          - Analytics data must be taken ONLY from the result of the ${AccountAnalyticsToolName.AccountAnalyticsData} or ${AccountAnalyticsToolName.AccountTopPerforming} tool. NEVER add any other keys!
          - The "inline widget"
          ${userHasAnalyticsWriteRole ? `- A suggestion to add this "inline widget" into a dashboard (This suggestion should always come AFTER the widget )` : ``}

    4. How to add an "inline widget" to account dashboard:
      - first check if a dashboard widget like that already exists, if it does, just highlight widget immediately
      - if it doesn't, ask the user weather to create a new dashboard and add the widget to it OR to add the widget to an existing **NOT read only dashboard**
      - then highlight the dashboard widget
  `;

  const contexts = [accountAnalyticsContext(store)];

  const tools = [
    navigateToAccountDashboardTool(store),

    // Analytics & Best Practices
    getBestPracticesTool(),
    accountTopPerformingTool(store),
    accountAnalyticsDataTool(store),
    getAccountFilterValuesTool(store),
    accountAnalyticsTableDataTool(store),
    accountWorstPerformingTool(store),

    // Dashboard tools
    highlightAccountWidgetTool(store),
    checkAccountWidgetExistsTool(store),

    ...(userHasAnalyticsWriteRole
      ? [
          createAccountDashboardTool(),
          renameAccountDashboardTool(store),
          deleteAccountDashboardTool(store),
          deleteAccountWidgetTool(store),
          updateAccountWidgetTool(store),
          createAccountWidgetTool(store),
        ]
      : []),
  ];

  return agent({
    name: AgentName.AccountAnalytics,
    role,
    store,
    conversationStarters,
    inScope,
    outOfScope,
    instructions,
    tools,
    widgetSchemas: [inlineWidgetSchema],
    contexts,
  });
}

export default accountAnalyticsAgent;
